import React from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { STATUS_COLORS } from "./palette";
import { EmptyState } from "@/components/ui";
import { PieChart as PieIcon } from "lucide-react";

const STATUS_LABELS: Record<string, string> = {
  new: "New",
  read: "Read",
  starred: "Starred",
  archived: "Archived",
  spam: "Spam",
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

export interface StatusDonutChartProps {
  data: Record<string, number>;
  height?: number;
}

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  return (
    <div className="bg-white rounded-lg border border-borderGreyColor shadow-lg px-3 py-2 font-dmsans flex items-center gap-2">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: entry.payload.fill }} />
      <span className="text-xs font-semibold text-blackDark tabular-nums">{entry.value}</span>
      <span className="text-xs text-iconTextGreyColor">{entry.name}</span>
    </div>
  );
};

export const StatusDonutChart: React.FC<StatusDonutChartProps> = ({ data, height = 260 }) => {
  const entries = Object.entries(data)
    .filter(([, count]) => count > 0)
    .map(([status, count]) => ({
      status,
      name: STATUS_LABELS[status] ?? status,
      value: count,
      fill: STATUS_COLORS[status] ?? "#898781",
    }));

  const total = entries.reduce((sum, e) => sum + e.value, 0);

  if (total === 0) {
    return (
      <div style={{ height }} className="flex items-center justify-center">
        <EmptyState icon={PieIcon} title="No submissions yet" description="Status breakdown will appear once forms start receiving responses." />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <div style={{ width: height, height, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={entries}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              startAngle={90}
              endAngle={-270}
              innerRadius={height * 0.28}
              outerRadius={height * 0.42}
              paddingAngle={1}
              minAngle={3}
              stroke="#fff"
              strokeWidth={2}
            >
              {entries.map((e) => (
                <Cell key={e.status} fill={e.fill} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex-1 min-w-0 space-y-1.5">
        {entries.map((e) => (
          <div key={e.status} className="flex items-center justify-between gap-2 text-xs font-dmsans">
            <span className="flex items-center gap-1.5 min-w-0 text-iconTextGreyColor">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: e.fill }} />
              <span className="truncate">{e.name}</span>
            </span>
            <span className="text-blackDark font-semibold tabular-nums flex-shrink-0">
              {e.value} <span className="text-iconTextGreyColor font-normal">({Math.round((e.value / total) * 100)}%)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatusDonutChart;
